<template>
  <div class="modal-content">
    <div class="modal-modal" v-if="isTermsModalVisible">
      <div>
        <p>{{ _('Please note that the translation is an AI-based machine translation.') }} {{ _('The AI may make false assumptions about the source text, so always check or have someone else check the translation before publishing.') }}</p>
        <p v-if="hasReTranslations">{{ _('You will also be charged for the fields you have selected to be retranslated.') }}</p>
        <div class="flex-row center">
          <button class="btn btn-success" type="button" v-on:click="acceptTerms"><i class="fa fa-fw fa-check"></i> {{ _('Translate') }}</button>
          <button class="btn btn-default" type="button" v-on:click="rejectTerms"><i class="fa fa-fw fa-ban"></i> {{ _('Cancel') }}</button>
        </div>
      </div>
    </div>
    <div class="modal-header">
      <h4 class="modal-title">{{ title }} ({{ getVersionName(settings.locale) + ' - ' + getVersionName(version) }})</h4>
      <span class="close" v-on:click="close"><i class="fa fa-close fa-fw"></i></span>
    </div>
    <div class="modal-body">
      <div class="translation-existing form-group"><input id="useExistingTranslationsInput" type="checkbox" v-model="useExistingTranslations" /><label for="useExistingTranslationsInput">{{ _('Use existing translations if available.') }}</label></div>
      <div class="translation-items">
        <div class="translation-item" v-for="translation in nonEmptyTranslations">
          <div class="translation-select">
            <input type="checkbox" v-model="selectedFields" :value="translation.field" />
          </div>
          <div class="translation-content">
            <div class="translation-label">{{ translation.title }}</div>
            <div v-html="translation.origText" class="translation-text translation-original" v-if="!(translation.text && useExistingTranslations)"></div>
            <div v-if="translation.text" class="translation-translated-wrap">
              <span class="label label-default">{{ _('Existing translation') }}</span>
              <div v-html="translation.text" class="translation-text translation-translated"></div>
            </div>
          </div>
          <div class="translation-cost" v-if="(!translation.text || !useExistingTranslations)">
            {{ translation.cost.toString().replace('.',',') + ' ' + currencySymbol(settings.currency) }}
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <div class="flex-row button-col space-between">
        <div class="flex-row button-col">
          <div class="button-col translation-button-col flex-row" v-if="hasExistingTranslations">
            <div class="btn-group dropup" v-if="hasExistingTranslations">
              <Dropdown buttonClass="btn-default" menuClass="dropdown-menu-right" direction="up">
                <template #trigger>
                  <i class="fa fa-check-square fa-fw"></i><span class="hidden-xs">{{ _('Select...') }} &nbsp;</span><span class="caret"></span>
                </template>
                <li><a href="javascript:;" v-on:click="selectAll">{{ _('Select all') }}</a></li>
                <li><a href="javascript:;" v-on:click="selectUntranslated">{{ _('Select untranslated') }}</a></li>
              </Dropdown>
            </div>
          </div>
          <button type="button" class="btn btn-info" v-else v-on:click="selectAll"><i class="fa fa-check-square fa-fw"></i> {{ _('Select all') }}</button>
        </div>
        <div class="button-col translation-button-col flex-row">
          <div class="translation-price"><span>{{ _('Price') }}: </span><strong>{{ $filters.currency(totalPrice, settings.currency) }}</strong></div>
          <button type="button" class="btn btn-success" v-on:click="translate" :disabled="!isTranslateButtonEnabled"><i class="fa fa-check fa-fw" v-if="!processingTranslations"></i><i class="fa fa-cog fa-spin fa-fw" v-else></i>{{ _('Translate') }}</button>
          <button type="button" class="btn btn-default hidden-xs" v-on:click="close"><i class="fa fa-close fa-fw"></i> {{ _('Close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-modal {
  position: absolute;
  background: white;
  left: 0;
  top: 0; 
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  padding: 20px;
}
.modal-modal > div {
  text-align: center;
}
</style>

<script>
import { mapGetters } from 'vuex'
import currencies from '~/lib/currencies'
import constants from '~/lib/constants'
import errors from '~/lib/errors'
import axios from 'axios'

export default {
  data () {
    return {
      useExistingTranslations: true,
      processingTranslations: false,
      translations: [],
      selectedFields: [],
      isTermsModalVisible: false,
      termsAccepted: false
    }
  },
  props: ['title', 'settings', 'shopId', 'parentType', 'parent', 'apiKey'],
  mixins: [currencies, constants, errors],
  computed: {
    ...mapGetters(['baseDomain', 'locale', 'version']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    shortVersion () {
      return this.version.split('_')[0]
    },
    isTranslateButtonEnabled () {
      return !this.processingTranslations && this.selectedFields && this.selectedFields.length
    },
    totalPrice() {
      return this.nonEmptyTranslations.filter(t => this.selectedFields.includes(t.field)).map(t => (this.useExistingTranslations && t.text ? 0 : t.cost)).reduce((a, e) => a += parseFloat(e), 0)
    },
    hasExistingTranslations () {
      if (this.translations.some(item => !!item.text)) {
        return true
      }
      return false
    },
    nonEmptyTranslations () {
      return this.translations.filter(t => !!t.origText)
    },
    fieldsToTranslate () {
      if (!this.useExistingTranslations) {
        return this.selectedFields
      }
      const translatedFields = this.nonEmptyTranslations.filter(t => !!t.text).map(t => t.field)
      return this.selectedFields.filter(f => !translatedFields.includes(f))
    },
    hasReTranslations () {
      if (this.useExistingTranslations || !this.hasExistingTranslations) {
        return false
      }
      const translatedFields = this.translations.filter(t => t.text).map(t => t.field)
      for (let j = 0; j < translatedFields.length; j++) {
        if (this.selectedFields.includes(translatedFields[j])) {
          return true
        }
      }
      return false
    }
  },
  methods: {
    selectAll () {
      this.selectedFields = this.nonEmptyTranslations.map(t => t.field)
    },
    selectUntranslated () {
      this.selectedFields = this.nonEmptyTranslations.filter(t => !t.text).map(t => t.field)
    },
    acceptTerms () {
      this.EventBus.$emit('translationTermsAccepted')
    },
    rejectTerms () {
      this.EventBus.$emit('translationTermsRejected')
    },
    acceptTermsDialog () {
      return new Promise((resolve, reject) => {
        this.isTermsModalVisible = true
        this.EventBus.$on('translationTermsAccepted', () => {
          this.isTermsModalVisible = false
          this.termsAccepted = true
          this.EventBus.$off('translationTermsAccepted')
          resolve()
        })
        this.EventBus.$on('translationTermsRejected', () => {
          this.isTermsModalVisible = false
          this.termsAccepted = false
          this.EventBus.$off('translationTermsAccepted')
          reject()
        })
      })
    },
    async loadTranslations () {
      if (!this.parent || !this.parent.id) {
        this.close()
        return
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/' + this.parentType + '/' + this.parent.id + '/translations.json?apikey=' + this.apiKey + '&target_lang=' + this.shortVersion)
      } catch(e) {
        this.translations = []
        return
      }
      this.translations = response.data
    },
    async translate() {
      if (!this.termsAccepted) {
        try {
          await this.acceptTermsDialog()
        } catch(e) {
          return
        }
      }
      this.processingTranslations = true
      let response
      if (this.fieldsToTranslate.length) {
        const data = {
          source_lang: this.settings.locale,
          target_lang: this.version,
          fields: this.fieldsToTranslate.join(',')
        }
        try {
          response = await axios.post(this.baseDomain + this.shopId + '/' + this.parentType + '/' + this.parent.id + '/translations.json?apikey=' + this.apiKey, data)
        } catch(e) {
          this.processingTranslations = false
          return
        }
      }
      this.processingTranslations = false
      this.finish(response)
    },
    close () {
      this.$emit('close')
    },
    finish (response) {
      const ret = []
      let translated = []
      if (response && response.data && response.data.translations) {
        translated = response.data.translations
      }
      if (translated.length != this.fieldsToTranslate.length) {
        this.showError(this._('Translation failed. Please try again.'))
        return
      }
      const done = {}
      let i
      for (i = 0; i < translated.length; i++) {
        ret.push({
          field: translated[i].field,
          text: translated[i].text
        })
        done[translated[i].field] = true
      }
      if (this.useExistingTranslations) {
        for (i = 0; i < this.selectedFields.length; i++) {
          if (done[this.selectedFields[i]]) {
            continue
          }
          for (let j = 0; j < this.translations.length; j++) {
            if (this.translations[j].field === this.selectedFields[i]) {
              ret.push(this.translations[j])
              break
            }
          }
        }
      }
      this.$emit('result', ret)
    }
  },
  mounted () {
    this.loadTranslations()
  }
}  

</script>